import React, { useState, useEffect, useCallback, useRef } from 'react';
import { contentAPI, bookmarksAPI } from '../services/api';
import ArticleCard from './ArticleCard';
import VideoCard from './VideoCard';
import LoadingScreen from './LoadingScreen';
import { WarningIcon } from './Icons';
import styles from '../styles/ContentListView.module.css';

/**
 * All Learning Resources View Component
 * Displays both articles and videos with unified search and infinite scroll
 *
 * Props:
 * - onBack: Callback when back button is clicked
 * - onArticleClick: Callback when article is clicked (slug)
 * - onVideoClick: Callback when video is clicked (video data)
 */
function AllLearningResourcesView({ onBack, onArticleClick, onVideoClick }) {
  const [resources, setResources] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'bookmarked'

  const searchTimeoutRef = useRef(null);
  const listContainerRef = useRef(null);

  // Load learning resources (articles + videos)
  const loadResources = useCallback(async (search = '') => {
    try {
      setIsLoading(true);
      setError(null);

      if (activeTab === 'bookmarked') {
        // Load all bookmarks
        const bookmarkData = await bookmarksAPI.getBookmarks();

        if (bookmarkData.length === 0) {
          setResources([]);
          return;
        }

        // Separate article and video bookmarks
        const articleBookmarks = bookmarkData.filter(b => b.content_type === 'article');
        const videoBookmarks = bookmarkData.filter(b => b.content_type === 'video');

        // Fetch ONLY bookmarked content by ID (more efficient than fetching ALL and filtering)
        const articlePromises = articleBookmarks.map(b =>
          contentAPI.getArticle(b.content_id).catch(err => {
            console.warn(`Failed to load article ${b.content_id}:`, err);
            return null;
          })
        );

        const videoPromises = videoBookmarks.map(b =>
          contentAPI.getVideo(b.content_id).catch(err => {
            console.warn(`Failed to load video ${b.content_id}:`, err);
            return null;
          })
        );

        const [articles, videos] = await Promise.all([
          Promise.all(articlePromises),
          Promise.all(videoPromises)
        ]);

        // Filter out null results and add contentType
        const bookmarkedArticles = articles
          .filter(article => article !== null)
          .map(article => ({ ...article, contentType: 'article', isBookmarked: true }));

        const bookmarkedVideos = videos
          .filter(video => video !== null)
          .map(video => ({ ...video, contentType: 'video', isBookmarked: true }));

        let combined = [...bookmarkedArticles, ...bookmarkedVideos];

        // Apply search filter if provided
        if (search && search.trim()) {
          const searchLower = search.toLowerCase();
          combined = combined.filter(item => {
            const title = (item.title || '').toLowerCase();
            const description = (item.description || item.excerpt || '').toLowerCase();
            const courseName = (item.courseName || '').toLowerCase();
            return title.includes(searchLower) ||
                   description.includes(searchLower) ||
                   courseName.includes(searchLower);
          });
        }

        // Sort by bookmark date (most recent first)
        combined.sort((a, b) => {
          const aBookmark = bookmarkData.find(bm =>
            bm.content_type === a.contentType && bm.content_id === a.id
          );
          const bBookmark = bookmarkData.find(bm =>
            bm.content_type === b.contentType && bm.content_id === b.id
          );
          return new Date(bBookmark.created_at) - new Date(aBookmark.created_at);
        });

        setResources(combined);
      } else {
        // Load all resources
        const data = await contentAPI.getLearningResources(search);

        // Also load bookmarks to mark which items are bookmarked
        const bookmarkData = await bookmarksAPI.getBookmarks();

        // Mark bookmarked items
        const enrichedData = data.map(item => {
          const isBookmarked = bookmarkData.some(b =>
            b.content_type === item.contentType && b.content_id === item.id
          );
          return { ...item, isBookmarked };
        });

        setResources(enrichedData);
      }
    } catch (err) {
      console.error('[AllLearningResourcesView] Error loading resources:', err);
      setError(err.message || 'Failed to load learning resources');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [activeTab]);

  // Initial load and reload when tab changes
  useEffect(() => {
    loadResources();
  }, [loadResources, activeTab]);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      loadResources(searchQuery);
    }, 500); // 500ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, loadResources]);

  // Pull to refresh handler
  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    loadResources(searchQuery);
  }, [searchQuery, loadResources]);

  // Pull to refresh detection
  useEffect(() => {
    const container = listContainerRef.current;
    if (!container) return;

    let startY = 0;
    let currentY = 0;

    const handleTouchStart = (e) => {
      if (container.scrollTop === 0) {
        startY = e.touches[0].clientY;
      }
    };

    const handleTouchMove = (e) => {
      if (container.scrollTop === 0 && !isRefreshing) {
        currentY = e.touches[0].clientY;
        const pullDistance = currentY - startY;

        if (pullDistance > 80) {
          handleRefresh();
        }
      }
    };

    container.addEventListener('touchstart', handleTouchStart);
    container.addEventListener('touchmove', handleTouchMove);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isRefreshing, handleRefresh]);

  // Handle content click
  const handleContentClick = (resource) => {
    if (resource.contentType === 'article') {
      onArticleClick?.(resource.slug, { title: resource.title, category: resource.category });
    } else if (resource.contentType === 'video') {
      onVideoClick?.(resource);
    }
  };

  return (
    <div className={styles.contentListView}>
      <div className={styles.listHeader}>
        <button onClick={onBack} className={styles.backButton}>
          ← Back
        </button>
        <h1 className={styles.listTitle}>Learning Resources</h1>
      </div>

      {/* Tabs */}
      <div className={styles.tabsContainer}>
        <button
          className={`${styles.tab} ${activeTab === 'all' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'bookmarked' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('bookmarked')}
        >
          Bookmarked
        </button>
      </div>

      <div className={styles.searchContainer}>
        <div className={styles.searchInputWrapper}>
          <input
            type="text"
            placeholder="Search articles and videos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className={styles.clearSearchButton}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div ref={listContainerRef} className={styles.listContainer}>
        {isRefreshing && (
          <div className={styles.refreshIndicator}>
            <div className={styles.refreshSpinner}></div>
          </div>
        )}

        {isLoading && !isRefreshing ? (
          <LoadingScreen text="Loading resources..." variant="dots" />
        ) : error ? (
          <div className={styles.errorContainer}>
            <div className={styles.errorIcon}>
              <WarningIcon size={64} />
            </div>
            <div className={styles.errorText}>{error}</div>
            <button onClick={() => loadResources(searchQuery)} className={styles.retryButton}>
              Retry
            </button>
          </div>
        ) : resources.length === 0 ? (
          <div className={styles.emptyContainer}>
            <div className={styles.emptyText}>
              {searchQuery ? 'No resources found' : 'No resources available'}
            </div>
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className={styles.clearSearchButtonLarge}>
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <div className={styles.cardGrid}>
            {resources.map((resource) => (
              <div
                key={`${resource.contentType}-${resource.id}`}
                onClick={() => handleContentClick(resource)}
                className={styles.cardWrapper}
              >
                {resource.contentType === 'article' ? (
                  <ArticleCard
                    id={resource.id}
                    title={resource.title}
                    slug={resource.slug}
                    category={resource.category}
                    heroImage={resource.heroImage}
                    excerpt={resource.excerpt}
                    isBookmarked={resource.isBookmarked}
                  />
                ) : (
                  <VideoCard
                    id={resource.id}
                    videoId={resource.videoId}
                    title={resource.title}
                    courseName={resource.courseName}
                    description={resource.description}
                    videoUrl={resource.videoUrl}
                    lessonUrl={resource.lessonUrl}
                    thumbnailUrl={resource.thumbnailUrl}
                    isBookmarked={resource.isBookmarked}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AllLearningResourcesView;
